import React, { useState, useEffect } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { Subheading } from "components/misc/Headings";

// Styled Components
const Card = styled.div`
  ${tw`bg-white px-6 py-8 sm:p-10 flex flex-col items-center text-center w-full rounded-xl`}
`;

const Temperature = styled.p`
  ${tw`text-4xl font-bold mt-2`}
`;

const Condition = styled.p`
  ${tw`text-lg text-gray-600 capitalize mt-1`}
`;

const ForecastContainer = styled.div`
  ${tw`
    mt-6 
    w-full 
    flex 
    sm:grid 
    sm:grid-cols-4 
    md:grid-cols-5 
    lg:grid-cols-7 
    gap-4 
    overflow-x-auto 
    sm:overflow-x-visible 
    pb-1
    pt-2
    px-1
    justify-start
  `}
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ForecastItem = styled.div`
  ${tw`
    flex flex-col items-center justify-center 
    p-3 sm:p-4 lg:p-5
    bg-gray-500 rounded-xl 
    min-w-[120px]
    shadow 
    text-sm
    w-full
    border border-gray-200
  `}
`;

const Day = styled.p`
  ${tw`text-white font-semibold`}
`;

const MinMaxTemp = styled.p`
  ${tw`text-white flex gap-1 text-xs lg:text-sm`}
`;

const WeatherIcon = styled.img`
  ${tw`w-10 h-10 mt-2`}
`;

const WeatherLink = styled.a`
  ${tw`mt-6 font-semibold hover:underline text-blue-600`}
`;

const HeadingRow = tw(Subheading)`text-xl sm:text-2xl font-bold mb-2`;


// Component
const WeatherCard = ({ lat, lon, destinationName }) => {
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!lat || !lon) return;

    const fetchWeather = async () => {
      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&daily=temperature_2m_max,temperature_2m_min,weathercode&timezone=auto`
        );
        const data = await response.json();

        if (!data.daily || !data.daily.temperature_2m_max) {
          throw new Error("Weather data is incomplete");
        }

        setWeather({
          currentTemp: data.current_weather?.temperature ?? "N/A",
          condition: getCondition(data.current_weather?.weathercode ?? 0),
          minTemp: data.daily.temperature_2m_min[0] ?? "N/A",
          maxTemp: data.daily.temperature_2m_max[0] ?? "N/A",
          forecast: data.daily.temperature_2m_max.map((maxTemp, index) => ({
            day: getDayName(index),
            minTemp: data.daily.temperature_2m_min[index] ?? "N/A",
            maxTemp: maxTemp ?? "N/A",
            weatherCode: data.daily.weathercode[index] ?? 0,
          })),
        });
      } catch (error) {
        console.error("Error fetching weather data:", error);
        setError(error.message);
      }
    };

    fetchWeather();
  }, [lat, lon]);

  const getCondition = (code) => {
    const conditions = {
      0: "Clear Sky",
      1: "Mainly Clear",
      2: "Partly Cloudy",
      3: "Overcast",
      45: "Foggy",
      48: "Dense Fog",
      51: "Light Drizzle",
      53: "Moderate Drizzle",
      55: "Heavy Drizzle",
      61: "Light Rain",
      63: "Moderate Rain",
      65: "Heavy Rain",
      80: "Scattered Showers",
      81: "Rain Showers",
      82: "Heavy Rain Showers",
      95: "Thunderstorms",
      96: "Thunderstorms with Hail",
      99: "Severe Thunderstorms",
    };
    return conditions[code] || "Unknown Weather";
  };

  const getWeatherIcon = (code) => {
    const iconMap = {
      0: "https://openweathermap.org/img/wn/01d.png",
      1: "https://openweathermap.org/img/wn/02d.png",
      2: "https://openweathermap.org/img/wn/03d.png",
      3: "https://openweathermap.org/img/wn/04d.png",
      45: "https://openweathermap.org/img/wn/50d.png",
      48: "https://openweathermap.org/img/wn/50d.png",
      51: "https://openweathermap.org/img/wn/09d.png",
      53: "https://openweathermap.org/img/wn/09d.png",
      55: "https://openweathermap.org/img/wn/09d.png",
      61: "https://openweathermap.org/img/wn/10d.png",
      63: "https://openweathermap.org/img/wn/10d.png",
      65: "https://openweathermap.org/img/wn/10d.png",
      80: "https://openweathermap.org/img/wn/09d.png",
      81: "https://openweathermap.org/img/wn/09d.png",
      82: "https://openweathermap.org/img/wn/11d.png",
      95: "https://openweathermap.org/img/wn/11d.png",
      96: "https://openweathermap.org/img/wn/11d.png",
      99: "https://openweathermap.org/img/wn/11d.png",
    };
    return iconMap[code] || "https://openweathermap.org/img/wn/50d.png";
  };

  const getDayName = (index) => {
    const today = new Date();
    today.setDate(today.getDate() + index);
    return today.toLocaleDateString("en-US", { weekday: "short" });
  };

  if (error) {
    return (
      <Card>
        <p>Error: {error}</p>
      </Card>
    );
  }

  return (
    <Card>
      {weather ? (
        <>
          <HeadingRow>{"Weather @ " + destinationName}</HeadingRow>
          <Temperature>{weather.currentTemp}°C</Temperature>
          <Condition>{weather.condition}</Condition>

          <p tw="text-gray-700 mt-2">
            Max: {weather.maxTemp}°C | Min: {weather.minTemp}°C
          </p>

          <h3 tw="text-lg font-semibold mt-6 mb-2">7-Day Forecast</h3>
          <ForecastContainer>
            {weather.forecast.map((day, index) => (
              <ForecastItem key={index}>
                <Day>{day.day}</Day>
                <WeatherIcon src={getWeatherIcon(day.weatherCode)} alt="Weather Icon" />
                <MinMaxTemp>
                  <span>{day.maxTemp}°C</span> | <span>{day.minTemp}°C</span>
                </MinMaxTemp>
              </ForecastItem>
            ))}
          </ForecastContainer>

          <WeatherLink
            href={`https://www.google.com/search?q=weather+in+${destinationName}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Detailed Weather →
          </WeatherLink>
        </>
      ) : (
        <p>Loading weather...</p>
      )}
    </Card>
  );
};

export default WeatherCard;
